import React, { createContext, useContext, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import styled from "styled-components";
import * as Yup from "yup";
import {
  SelectField,
  TextInputField,
  RadioField,
  RadioFieldGroup,
  RichTextField,
  FileUploadField,
} from "./library/Form";
import MultiPanelForm from "./MultiPanelForm";
import ApplicationRecord from "../apiResources/ApplicationRecord";
import CheckEmailValidity from "../apiResources/CheckEmailValidity";

// Context to share fetched notice periods
const NoticePeriodsContext = createContext<string[]>([]);

const defaultInitialValues = {
  firstName: "",
  lastName: "",
  contactNumber: "",
  email: "",
  location: "",
  nationality: "",
  gender: "",
  hasDrivingLicence: "",

  jobType: "permanent",
  currentEmployer: "",
  currentJobTitle: "",
  currentSalary: null,
  desiredSalary: null,

  cv: null,
  additionalInfo: "",

  noticePeriod: "",
  noticePeriodOther: "",
};

const personalDetailsValidation = Yup.object({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Required"),
  contactNumber: Yup.string(),
  location: Yup.string().required("Required"),
  nationality: Yup.string(),
  gender: Yup.string(),
  hasDrivingLicence: Yup.string().required("Required"),
});

const jobDetailsValidation = Yup.object({
  currentEmployer: Yup.string(),
  currentJobTitle: Yup.string(),
  currentSalary: Yup.number()
    .typeError("Please enter a number")
    .integer("Please enter a number without decimals")
    .positive("Please enter a number greater than zero")
    .nullable(),
  desiredSalary: Yup.number()
    .typeError("Please enter a number")
    .integer("Please enter a number without decimals")
    .positive("Please enter a number greater than zero")
    .required("Required"),
  noticePeriod: Yup.string(),
  noticePeriodOther: Yup.string().when("noticePeriod", {
    is: "Other",
    then: (schema) =>
      schema
        .required("Please specify your notice period")
        .max(50, "Maximum 50 characters"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const extraDetailsValidation = Yup.object({
  cv: Yup.object({
    filename: Yup.string(),
    data: Yup.string(),
  })
    .nullable()
    .required("Required"),
  additionalInfo: Yup.string()
    .required("Required")
    .strict(true)
    .typeError("Additional Information is Required"),
});

const CandidateForm = (props: Props) => {
  const { initialValues = defaultInitialValues, onSubmit } = props;
  const [noticePeriods, setNoticePeriods] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch notice periods from backend
  useEffect(() => {
    fetch("/api/v1/candidates/notice_periods", {
      headers: { Accept: "application/vnd.api+json" },
      credentials: "same-origin",
    })
      .then((res) => res.json())
      .then((data) => {
        const fromApi =
          data && data.meta && Array.isArray(data.meta.notice_periods)
            ? (data.meta.notice_periods as string[])
            : [];
        setNoticePeriods(fromApi);
      })
      .catch(() => {
        setNoticePeriods([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  Object.keys(initialValues).forEach((key) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    initialValues[key] = initialValues[key] ?? defaultInitialValues[key];
  });

  // Check if notice period is custom (not in fetched list) - only after loading
  if (!isLoading && initialValues.noticePeriod && noticePeriods.length > 0) {
    const isCustom = !noticePeriods.includes(initialValues.noticePeriod);
    if (isCustom) {
      initialValues.noticePeriodOther = initialValues.noticePeriod;
      initialValues.noticePeriod = "Other";
    }
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <NoticePeriodsContext.Provider value={noticePeriods}>
      <MultiPanelForm
        initialValues={initialValues}
        validationSchemas={[
          personalDetailsValidation,
          jobDetailsValidation,
          extraDetailsValidation,
        ]}
        onSubmit={(values) => onSubmit(coerceIntegers(values))}
      >
        <PersonalDetailsPanel />
        <JobDetailsPanel />
        <ExtraDetailsPanel />
      </MultiPanelForm>
    </NoticePeriodsContext.Provider>
  );
};

const PersonalDetailsPanel = () => {
  const { setFieldError, errors, values } = useFormikContext<FormValues>();

  const validateEmail = async () => {
    // There is no point doing the check if we are already showing an email, so
    // we can early abort.
    if (errors.email) {
      return;
    }

    const EmailValid = new CheckEmailValidity({
      email: values.email,
      resourceType: "candidate",
    });

    const isValid = await EmailValid.save();

    if (!isValid) {
      setFieldError("email", EmailValid.errors.email.fullMessage);
    }
  };

  return (
    <>
      <PanelHeading>Personal details</PanelHeading>
      <FieldsLayout>
        <TextInputField
          name="firstName"
          label="First Name"
          placeholder="John"
        />

        <TextInputField name="lastName" label="Last Name" placeholder="Smith" />

        <TextInputField
          type="email"
          name="email"
          label="Email"
          placeholder="john.smith@example.com"
          helpText="The candidate's email address will not be visible to any employers"
          onBlur={validateEmail}
        />

        <TextInputField
          type="tel"
          name="contactNumber"
          label="Contact Number"
          placeholder="07123456789 (optional)"
        />

        <TextInputField name="location" label="Location" placeholder="London" />

        <TextInputField
          name="nationality"
          label="Nationality"
          placeholder="British"
        />

        <StyledSelectField
          label="Gender"
          name="gender"
          helpText="Capturing the gender of the candidate allows the employer to understand the demographic of their candidate pool and improve their processes, for a more fair and representative recruitment process"
        >
          <option value="">Select your Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </StyledSelectField>

        <RadioFieldGroup label="Driving License" name="hasDrivingLicence">
          <RadioField label="Yes" name="hasDrivingLicence" value="true" />
          <RadioField label="No" name="hasDrivingLicence" value="false" />
        </RadioFieldGroup>
      </FieldsLayout>
    </>
  );
};

const JobDetailsPanel = () => {
  const { values } = useFormikContext<FormValues>();
  const noticePeriods = useContext(NoticePeriodsContext);

  const payType = values.jobType === "permanent" ? "Salary" : "Day Rate";
  const showOtherNoticePeriod = values.noticePeriod === "Other";

  return (
    <>
      <PanelHeading>Job details</PanelHeading>
      <FieldsLayout>
        <StyledSelectField label="Notice Period" name="noticePeriod">
          <option value="">Select Notice Period</option>
          {noticePeriods.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
          <option value="Other">Other</option>
        </StyledSelectField>

        {showOtherNoticePeriod && (
          <TextInputField
            name="noticePeriodOther"
            label="Please specify notice period"
            placeholder="e.g., 6 weeks, 90 days"
            maxLength={50}
          />
        )}
        <TextInputField
          name="currentEmployer"
          label="Current Employer"
          placeholder="Example Company Inc."
        />

        <TextInputField
          name="currentJobTitle"
          label="Current Job Title"
          placeholder="Developer"
        />

        <TextInputField
          name="currentSalary"
          label={`Current ${payType}`}
          placeholder="30000"
        />

        <TextInputField
          name="desiredSalary"
          label={`Desired ${payType}`}
          placeholder="30000"
        />
      </FieldsLayout>
    </>
  );
};

const ExtraDetailsPanel = () => (
  <>
    <PanelHeading>Other details</PanelHeading>

    <FileUploadField name="cv" label="CV File" accept=".pdf,.docx" />

    <RichTextField name="additionalInfo" label="Additional Information" />
  </>
);

type FormValues = ReturnType<() => typeof defaultInitialValues>;

/**
 * Ensure that current salary and desired salary are converted to type Number
 * so that any prefixed or trailing zeros (including .0) are removed.
 *
 * @param values The values submitted form the form.
 * @returns Form values with number types coerced to type Number.
 */
function coerceIntegers(values: FormValues) {
  const { currentSalary, desiredSalary, noticePeriod, noticePeriodOther } =
    values;
  const finalNoticePeriod =
    noticePeriod === "Other" ? noticePeriodOther : noticePeriod;
  return {
    ...values,
    currentSalary: currentSalary ? Number(currentSalary) : null,
    desiredSalary: desiredSalary ? Number(desiredSalary) : desiredSalary,
    noticePeriod: finalNoticePeriod,
    noticePeriodOther: undefined,
  };
}

type Props = {
  initialValues?: FormValues;
  onSubmit: (values: FormValues) => Promise<ApplicationRecord>;
};

const FieldsLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1.5rem;

  @media only screen and (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const PanelHeading = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
`;

const StyledSelectField = styled(SelectField)`
  background-color: white !important;
`;

export default CandidateForm;
